import React, { useState, useEffect } from 'react';

import { Button, PopUp, Dialog, DialogHeader, DialogContent, Input } from '@ergeon/core-components';

interface LinkDialogProps {
  isVisible: boolean;
  initialText: string;
  initialUrl?: string;
  onSave: (text: string, url: string) => void;
  onCancel: () => void;
}

export const LinkDialog: React.FC<LinkDialogProps> = ({
  isVisible,
  initialText,
  initialUrl = '',
  onSave,
  onCancel,
}) => {
  const [text, setText] = useState(initialText);
  const [url, setUrl] = useState(initialUrl);

  const isEditing = Boolean(initialUrl);

  // Reset form when dialog opens
  useEffect(() => {
    if (isVisible) {
      setText(initialText);
      setUrl(initialUrl);
    }
  }, [isVisible, initialText, initialUrl]);

  const handleSave = () => {
    if (url.trim()) {
      onSave(text || url, url);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      onCancel();
    }
  };

  return (
    <PopUp isVisible={isVisible} maxWidth="xsmall" onChangeVisible={onCancel}>
      <Dialog isVisible={isVisible}>
        <DialogHeader onDismiss={onCancel} title={isEditing ? 'Edit link' : 'Add link'} />
        <DialogContent>
          <div className="flex flex-col gap-4" onKeyDown={handleKeyDown}>
            <Input
              label="Text"
              onChange={(_e, _name, value) => setText(value || '')}
              placeholder="Link text"
              value={text} />
            <Input
              autoFocus
              label="URL"
              onChange={(_e, _name, value) => setUrl(value || '')}
              placeholder="https://example.com"
              value={url} />
            <div className="flex gap-2">
              <Button
                flavor="regular"
                isFullWidth
                onClick={onCancel}
                taste="line"
                type="button">
                Cancel
              </Button>
              <Button
                disabled={!url.trim()}
                flavor="primary"
                isFullWidth
                onClick={handleSave}
                type="button">
                {isEditing ? 'Update' : 'Add Link'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </PopUp>
  );
};
